/**
 * Box 6, the budget simulator (spec §5.5, plan R13): what a weekly allocation earns on the bundle's curves, with its
 * range across the sampled posterior draws, and "จัดงบอัตโนมัติ". Every figure is per week and media only. Pure.
 *
 * @module services/budget/simulator
 */
import { MAX_SHIFT } from './budgetContract';
import type { Bundle, Ranged } from './bundleSchema';
import { interpolate, rangedOf, slopeAt } from './curves';

/** Weekly spend per channel. */
export type Allocation = Readonly<Record<string, number>>;

/** A ratio over a budget under MIN_MOVE, which prints 0.00, is not measurable, so roas and marginal are null there (T9f). */
export interface Forecast {
  budget: number;
  outcome: Ranged;
  roas: Ranged | null;
  /** Σ (a_c ÷ budget) × slope_c: mROAS weighted by spend. */
  marginal: Ranged | null;
}

type Bounds = Readonly<Record<string, { min: number; max: number; sliderMax: number }>>;
type Curve = Bundle['curves'][string];

/** fmtMoney prints 2 decimals, so a smaller budget, outcome, amount or shift reads 0.00, and that is 0 (T9a, T9f). */
export const MIN_MOVE = 0.005;
/** Auto-allocate moves the budget B ÷ STEPS at a time. */
const STEPS = 1000;
/** A step still fits when it passes the max by under this share of a step, so float error never costs a channel its last whole step. */
const FIT = 1e-9;

const sum = (xs: readonly number[]): number => xs.reduce((s, x) => s + x, 0);
const totalOf = (b: Bundle, a: Allocation): number => sum(b.channels.map((c) => a[c.id] ?? 0));

export const currentAllocation = (b: Bundle): Allocation =>
  Object.fromEntries(b.channels.map((c) => [c.id, b.current_weekly[c.id]]));

/** R13's bounds: ±MAX_SHIFT of current, 0 for a stopped channel. A slider stops at the grid's top, so it never extrapolates. */
export const boundsOf = (b: Bundle): Bounds =>
  Object.fromEntries(b.channels.map((c) => {
    if (c.stopped) return [c.id, { min: 0, max: 0, sliderMax: 0 }];
    const now = b.current_weekly[c.id];
    const { grid } = b.curves[c.id];
    return [c.id, { min: (1 - MAX_SHIFT) * now, max: (1 + MAX_SHIFT) * now, sliderMax: grid[grid.length - 1] }];
  }));

/** read summed over the channels at a: on the mean curves, and on each sampled draw (draw d of every curve is one draw, R12). */
const summed = (b: Bundle, a: Allocation, read: (c: Curve, ys: readonly number[], x: number) => number): { mean: number; draws: number[] } => {
  const at = (ys: (c: Curve) => readonly number[]): number => sum(b.channels.map(({ id }) => read(b.curves[id], ys(b.curves[id]), a[id] ?? 0)));
  const draws = Object.values(b.curves)[0]?.draws.length ?? 0;
  return { mean: at((c) => c.mean), draws: Array.from({ length: draws }, (_, d) => at((c) => c.draws[d])) };
};

const outcomeAt = (c: Curve, ys: readonly number[], x: number): number => interpolate(c.grid, ys, x);

/** The media outcome at a under each sampled draw, in draw order, so two allocations pair draw by draw. */
export const drawTotals = (b: Bundle, a: Allocation): number[] => summed(b, a, outcomeAt).draws;

export const forecastOf = (b: Bundle, a: Allocation): Forecast => {
  const budget = totalOf(b, a);
  const out = summed(b, a, outcomeAt);
  // Spec §5.5: the range of the per-draw totals, never the channels' own ranges added up.
  const outcome = rangedOf(out.mean, out.draws);
  if (budget < MIN_MOVE) return { budget, outcome, roas: null, marginal: null };
  const m = summed(b, a, (c, ys, x) => (x / budget) * slopeAt(c.grid, ys, x));
  return {
    budget,
    outcome,
    // Dividing by a budget above 0 keeps outcome's lo ≤ mean ≤ hi, and one of MIN_MOVE or more keeps it finite.
    roas: { mean: outcome.mean / budget, lo: outcome.lo / budget, hi: outcome.hi / budget },
    marginal: rangedOf(m.mean, m.draws),
  };
};

/** The channel whose next step gains most on its mean curve without passing its max; a tie goes to the earlier channel. */
const bestStep = (b: Bundle, bounds: Bounds, alloc: ReadonlyMap<string, number>, step: number): string | undefined =>
  b.channels.reduce<{ ch: string; gain: number } | undefined>((best, { id }) => {
    const x = alloc.get(id) ?? 0;
    if (x + step > bounds[id].max + FIT * step) return best;
    const { grid, mean } = b.curves[id];
    const gain = interpolate(grid, mean, x + step) - interpolate(grid, mean, x);
    return best === undefined || gain > best.gain ? { ch: id, gain } : best;
  }, undefined)?.ch;

/**
 * Spec §5.5's "จัดงบอัตโนมัติ" (plan R13): the current total B, spent from the lower bounds in greedy steps of
 * B ÷ STEPS on the mean curves, each step to the channel that gains most; any remainder goes to the last best channel.
 */
export const autoAllocate = (b: Bundle): Allocation => {
  const current = currentAllocation(b);
  const budget = totalOf(b, current);
  // A budget that prints 0.00 has nothing to move (all zeros at B = 0), and under 2.47e-321 B ÷ STEPS is 0 (T9f).
  if (budget < MIN_MOVE) return current;
  const bounds = boundsOf(b);
  const step = budget / STEPS;
  // Built locally and never shared: the allocation as the steps land.
  const alloc = new Map<string, number>(b.channels.map((c) => [c.id, bounds[c.id].min]));
  // The lower bounds hold (1 − MAX_SHIFT) × B, so these are MAX_SHIFT × STEPS whole steps.
  const steps = Math.round((budget - sum([...alloc.values()])) / step);
  const last = Array.from({ length: steps }).reduce<string | undefined>((prev) => {
    const ch = bestStep(b, bounds, alloc, step);
    if (ch === undefined) return prev;
    alloc.set(ch, Math.min((alloc.get(ch) ?? 0) + step, bounds[ch].max));
    return ch;
  }, undefined);
  if (last !== undefined) {
    const rest = budget - sum([...alloc.values()]);
    alloc.set(last, Math.min((alloc.get(last) ?? 0) + rest, bounds[last].max));
  }
  return Object.fromEntries(b.channels.map((c) => [c.id, alloc.get(c.id) ?? 0]));
};
